import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Instagram, Twitter } from "lucide-react";
import CatalogueCTA from "./CatalogueCTA";
import DownloadBrochureButton from "./DownloadBrochureButton";
import RegistryRequestModal from "./RegistryRequestModal";

/**
 * Footer - magazine colophon with mobile-first organization.
 *
 * Mobile: brand lockup, scrollable trust band, prominent contact CTAs,
 * collapsible accordion sections, stacked bottom strip.
 * Desktop: 12-col directory with columns, centered trust band, brass corner
 * ornaments. Same content, different shape.
 */

const SECTIONS = [
  {
    id: "house",
    title: "The House",
    span: "span-house",
    type: "house",
  },
  {
    id: "routes",
    title: "Routes",
    links: [
      { label: "Persian Gulf", to: "presence" },
      { label: "Africa & Levant", to: "presence" },
      { label: "Asia Pacific", to: "presence" },
    ],
  },
  {
    id: "navigate",
    title: "Navigate",
    links: [
      { label: "About", to: "about" },
      { label: "Our Story", to: "/our-story" },
      { label: "Catalogue", to: "/catalogue" },
      { label: "Inquiry", to: "inquiry" },
    ],
  },
  {
    id: "desk",
    title: "Export Desk",
    span: "span-desk",
    type: "desk",
  },
];

const CERTS = ["FSSAI", "APEDA", "ISO 22000", "HACCP", "GLOBALG.A.P."];
const INSTAGRAM_URL = "https://www.instagram.com/viloraimpex?igsh=Z3VzOXY0ZHYzeWJr";
const X_URL = "https://x.com/viloraimpex";
const EXPORT_LINKS = [
  { label: "Rice Supplier in India", to: "/rice-supplier-in-india" },
  { label: "Rice Exporter from India", to: "/rice-exporter-from-india" },
  { label: "Basmati Rice Exporter from India", to: "/basmati-rice-exporter-from-india" },
  { label: "Non-Basmati Rice Exporter India", to: "/non-basmati-rice-exporter-india" },
  { label: "1401 Basmati Rice Exporter", to: "/1401-basmati-rice-exporter" },
  { label: "Sugandha Basmati Rice Exporter", to: "/sugandha-basmati-rice-exporter" },
  { label: "Sharbati Basmati Rice Exporter", to: "/sharbati-basmati-rice-exporter" },
  { label: "Traditional Basmati Rice Exporter", to: "/traditional-basmati-rice-exporter" },
  { label: "Pesticide-Free Basmati Rice", to: "/pesticide-free-basmati-rice-exporter" },
  { label: "Basmati Rice Price", to: "/basmati-rice-price" },
  { label: "Indian Rice Price", to: "/indian-rice-price" },
  { label: "Pulses Supplier from India", to: "/pulses-supplier-from-india" },
  { label: "Spices Exporter from India", to: "/spices-exporter-from-india" },
  { label: "Dry Fruits Exporter from India", to: "/dry-fruits-exporter-from-india" },
  { label: "Fresh Produce Exporter from India", to: "/fresh-produce-exporter-from-india" },
  { label: "Mango Exporter from India", to: "/mango-exporter-from-india" },
  { label: "Indian Agro Products Exporter", to: "/indian-agro-products-exporter" },
];

export default function Footer() {
  const [open, setOpen] = useState({ house: true });
  const [registryRequest, setRegistryRequest] = useState(null);
  const [footerSocialRef, footerSocialCue] = useSocialCue();

  const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));
  const goTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer id="footer" className="v-footer paper">
      <CornerOrnament className="v-footer-corner tl" />
      <CornerOrnament className="v-footer-corner tr" />

      <div className="v-footer-inner">
        {/* Brand lockup */}
        <div className="v-footer-brand">
          <img
            src="/logo.png"
            alt="Vilora Impex — Indian Trading House, Gujarat"
            width={132}
            height={132}
            className="mx-auto object-contain v-footer-logo"
            style={{ filter: "drop-shadow(0 0 22px rgba(168, 132, 61, 0.5))" }}
          />
          <div className="v-footer-wordmark">
            VILORA <em>IMPEX</em>
          </div>
          <div className="v-footer-tagline">- Of grain, of spice, of trust -</div>
        </div>

        {/* Trust band - certifications, horizontally scrollable on mobile */}
        <div
          className="v-trust-band"
          role="list"
          aria-label="Certifications and standards"
        >
          {CERTS.map((c) => (
            <span key={c} className="v-trust-chip" role="listitem">
              {c}
            </span>
          ))}
        </div>

        {/* Mobile primary contact CTA - invisible on desktop */}
        <div
          ref={footerSocialRef}
          className={`v-footer-cta ${footerSocialCue ? "social-cta-active" : ""}`}
        >
          <a href="mailto:exports@viloraimpex.com" aria-label="Email export desk">
            <span>Email Export Desk</span>
            <strong>exports@viloraimpex.com →</strong>
          </a>
          <a href="tel:+918669999901" aria-label="Call export desk">
            <span>Call Export Desk</span>
            <strong>+91 86699 99901 →</strong>
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Vilora Impex Instagram"
            className="social-cta-link"
          >
            <span className="social-cta-title">
              <Instagram size={15} strokeWidth={1.6} />
              Instagram
            </span>
            <strong>
              @viloraimpex
              <ExternalLink size={13} strokeWidth={1.7} />
            </strong>
            <span className="clickable-pill">Open</span>
          </a>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Vilora Impex on X"
            className="social-cta-link"
          >
            <span className="social-cta-title">
              <Twitter size={15} strokeWidth={1.6} />
              X
            </span>
            <strong>
              @viloraimpex
              <ExternalLink size={13} strokeWidth={1.7} />
            </strong>
            <span className="clickable-pill">Open</span>
          </a>
          {footerSocialCue ? (
            <div className="social-follow-message">Follow us on Instagram</div>
          ) : null}
        </div>
        <CatalogueCTA variant="dark" className="v-footer-catalogue" code="EXPORT" />
        <div className="v-footer-brochure-row">
          <DownloadBrochureButton variant="inline" label="Or download the 12-page PDF brochure" />
        </div>

        {/* Directory - accordion on mobile, columns on desktop */}
        <div className="v-footer-dir">
          {SECTIONS.map((s) => (
            <SectionBlock
              key={s.id}
              section={s}
              isOpen={!!open[s.id]}
              onToggle={() => toggle(s.id)}
              goTo={goTo}
            />
          ))}
        </div>

        <nav
          className="v-footer-export-links"
          aria-label="Export category landing pages"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          <div className="v-footer-export-links-head">
            <span className="folio">EXPORT PAGES</span>
            <span>Buyer-intent categories</span>
          </div>
          <ul>
            {EXPORT_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Registration row - Stanford guideline #2 (real organization).
            Empty values show as em-dashes; replace TODO_* with real numbers. */}
        <div className="v-footer-registry">
          <RegItem
            label="IEC"
            requestLabel="Request IEC"
            onRequest={() => setRegistryRequest({ type: "IEC", label: "IEC details" })}
          />
          <RegItem
            label="GSTIN"
            requestLabel="Request GST"
            onRequest={() => setRegistryRequest({ type: "GST", label: "GSTIN details" })}
          />
          <RegItem
            label="FSSAI"
            requestLabel="Request FSSAI"
            onRequest={() => setRegistryRequest({ type: "FSSAI", label: "FSSAI license" })}
          />
          <RegItem
            label="APEDA"
            requestLabel="Request APEDA"
            onRequest={() => setRegistryRequest({ type: "APEDA", label: "APEDA registration" })}
          />
        </div>

        {/* Bottom strip */}
        <div className="v-footer-strip">
          <span>© {new Date().getFullYear()} VILORA IMPEX · ALL RIGHTS RESERVED</span>
          <span className="finis">- finis -</span>
          <span>SET IN CORMORANT &amp; CRIMSON · MADE IN GUJARAT</span>
        </div>
      </div>

      <RegistryRequestModal
        open={!!registryRequest}
        documentType={registryRequest?.type}
        documentLabel={registryRequest?.label}
        onClose={() => setRegistryRequest(null)}
      />
    </footer>
  );
}

function useSocialCue() {
  const ref = useRef(null);
  const [showCue, setShowCue] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShowCue(true);
        timer = window.setTimeout(() => setShowCue(false), 2000);
        observer.disconnect();
      },
      { threshold: 0.4 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  return [ref, showCue];
}

function SectionBlock({ section, isOpen, onToggle, goTo }) {
  const className = `v-footer-section ${section.span || ""} ${
    isOpen ? "is-open" : ""
  }`.trim();

  return (
    <div className={className}>
      <button
        type="button"
        className="v-footer-section-toggle"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`v-foot-${section.id}`}
      >
        <span>{section.title}</span>
        <span className="chev" aria-hidden="true">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path
              d="M1 1l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="square"
            />
          </svg>
        </span>
      </button>

      <div id={`v-foot-${section.id}`} className="v-footer-section-body">
        {section.type === "house" ? <HouseBody /> : null}
        {section.type === "desk" ? <DeskBody /> : null}
        {section.links
          ? section.links.map((l) =>
              typeof l.to === "string" && l.to.startsWith("/") ? (
                <Link key={l.label} to={l.to} className="footer-link">
                  {l.label}
                </Link>
              ) : (
                <button key={l.label} type="button" onClick={() => goTo(l.to)}>
                  {l.label}
                </button>
              )
            )
          : null}
      </div>
    </div>
  );
}

function HouseBody() {
  return (
    <div>
      <p className="v-footer-house-copy">
        A subsidiary of <em>Vilora Organic Essential Pvt. Ltd.</em> Structured
        supply of grains, spices, dry goods and fresh produce - moved by hand,
        shipped by ledger.
      </p>
      <div className="v-footer-founders">
        <div className="v-footer-founder-row">
          <span className="label">Founder</span>
          <span className="name">D. Anadkat</span>
        </div>
        <div className="v-footer-founder-row">
          <span className="label">Co-founder</span>
          <span className="name">H. Dudhatra</span>
        </div>
        <div className="v-footer-founder-row">
          <span className="label">Established</span>
          <span className="name">2025 · Gujarat</span>
        </div>
      </div>
    </div>
  );
}

function DeskBody() {
  const [deskSocialRef, deskSocialCue] = useSocialCue();

  return (
    <div className="flex flex-col gap-2">
      <a href="mailto:exports@viloraimpex.com">exports@viloraimpex.com</a>
      <a href="tel:+918669999901">+91 86699 99901</a>
      <div
        ref={deskSocialRef}
        className={`social-link-stack ${deskSocialCue ? "social-cta-active" : ""}`}
      >
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="desk-social-link social-cta-link">
          <span className="desk-social-icon">
            <Instagram size={15} strokeWidth={1.7} />
          </span>
          <span className="desk-social-copy">
            <span className="desk-social-label">Instagram</span>
            <strong>@viloraimpex</strong>
          </span>
          <ExternalLink size={12} strokeWidth={1.7} />
        </a>
        <a href={X_URL} target="_blank" rel="noopener noreferrer" className="desk-social-link social-cta-link">
          <span className="desk-social-icon">
            <Twitter size={15} strokeWidth={1.7} />
          </span>
          <span className="desk-social-copy">
            <span className="desk-social-label">X</span>
            <strong>@viloraimpex</strong>
          </span>
          <ExternalLink size={12} strokeWidth={1.7} />
        </a>
        {deskSocialCue ? (
          <div className="social-follow-message social-follow-message--compact">
            Follow us on Instagram
          </div>
        ) : null}
      </div>
      <CatalogueCTA variant="dark" compact code="PDF" />
      <span style={{ color: "rgba(244,239,227,0.62)", fontSize: 15.5 }}>
        Rajkot, Gujarat - Mon–Sat · 09:00–19:00 IST
      </span>
    </div>
  );
}

function RegItem({ label, value, requestLabel, onRequest }) {
  const isPending = !value || value.startsWith("TODO_");
  if (requestLabel) {
    return (
      <div className="v-footer-reg-item">
        <span className="v-footer-reg-label">{label}</span>
        <button type="button" className="v-footer-reg-request" onClick={onRequest}>
          {requestLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="v-footer-reg-item">
      <span className="v-footer-reg-label">{label}</span>
      <span className="v-footer-reg-value">
        {isPending ? <span className="v-footer-reg-pending">- pending -</span> : value}
      </span>
    </div>
  );
}

function CornerOrnament({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
    >
      <line x1="2" y1="2" x2="14" y2="2" stroke="var(--brass)" strokeWidth="0.8" />
      <line x1="2" y1="2" x2="2" y2="14" stroke="var(--brass)" strokeWidth="0.8" />
      <circle cx="2" cy="2" r="2" fill="none" stroke="var(--brass)" strokeWidth="0.8" />
    </svg>
  );
}
